import { useCallback } from 'react';
import { apiGet } from '../../api/apiClient';
import { usePerWallet } from './usePerWallet';
import { tradesFromTs, tradesToTs } from './useWalletTrades';

interface PositionsResponse {
    points: {
        timestamp: string;
        outcome_index: number;
        open_position: number;
    }[];
}

export interface PositionPoint {
    t: number;
    v: number;
}

export interface WalletPosition {
    yes: PositionPoint[];
    no: PositionPoint[];
}

export interface WalletPositionsResult {
    positionsByWallet: ReadonlyMap<string, WalletPosition>;
    loadingWallets: ReadonlySet<string>;
    failedWallets: ReadonlySet<string>;
    retryWallet: (wallet: string) => void;
}

const REQUEST_TIMEOUT_MS = 30_000;

export function toWalletPosition(
    points: PositionsResponse['points']
): WalletPosition {
    const position: WalletPosition = { yes: [], no: [] };
    const sorted = [...points]
        .map(p => ({
            t: new Date(p.timestamp).getTime(),
            v: Number(p.open_position),
            side: p.outcome_index,
        }))
        .filter(p => Number.isFinite(p.t) && Number.isFinite(p.v))
        .sort((a, b) => a.t - b.t);
    for (const p of sorted) {
        const list = p.side === 1 ? position.no : position.yes;
        const last = list[list.length - 1];
        if (last && last.t === p.t) last.v = p.v;
        else list.push({ t: p.t, v: p.v });
    }
    return position;
}

export function useWalletPositions(
    marketId: string | null,
    wallets: readonly string[],
    start: Date | null = null,
    closedEnd: Date | null = null
): WalletPositionsResult {
    const fromTs = tradesFromTs(start);
    const toTs = tradesToTs(closedEnd);
    const scope = marketId ? `${marketId}|${fromTs ?? ''}|${toTs ?? ''}` : null;

    const load = useCallback(
        async (
            wallet: string,
            signal: AbortSignal
        ): Promise<WalletPosition> => {
            const params = new URLSearchParams();
            if (fromTs) params.set('from_ts', fromTs);
            if (toTs) params.set('to_ts', toTs);
            const res = await apiGet<PositionsResponse>(
                `/api/markets/${encodeURIComponent(marketId ?? '')}/trader/${encodeURIComponent(wallet)}/positions?${params}`,
                signal
            );
            return toWalletPosition(res.points ?? []);
        },
        [marketId, fromTs, toTs]
    );

    const result = usePerWallet(scope, wallets, load, REQUEST_TIMEOUT_MS);

    return {
        positionsByWallet: result.byWallet,
        loadingWallets: result.loadingWallets,
        failedWallets: result.failedWallets,
        retryWallet: result.retryWallet,
    };
}
